import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Megaphone, Plus, Edit2, Trash2, Eye, EyeOff, Loader2, X, Users, Sparkles, AlertTriangle, Info, Gift, Clock, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { API_URL as API } from '@/utils/apiConfig';


const STYLES = [
  { key: 'info', label: 'Info', icon: Info, color: '#6366f1', bg: 'rgba(99,102,241,0.12)' },
  { key: 'feature', label: 'New Feature', icon: Sparkles, color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  { key: 'promo', label: 'Promotion', icon: Gift, color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  { key: 'warning', label: 'Important', icon: AlertTriangle, color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
];

const AUDIENCES = [
  { key: 'all', label: 'All Users' },
  { key: 'guest', label: 'Guest Users Only' },
  { key: 'free', label: 'Free Users Only' },
  { key: 'paid', label: 'Paid Users Only' },
  { key: 'premium', label: 'Premium Users Only' },
];

const DISPLAY_LOCATIONS = [
  { key: 'modal', label: 'Popup Modal' },
  { key: 'banner', label: 'Chat Banner' },
  { key: 'chat_message', label: 'Chat System Message' },
  { key: 'push', label: 'Push Notification' },
];

// Page keys the frontend recognises when filtering in-app broadcasts.
// Must stay in sync with the pathToPageKey mapping used by
// BroadcastModal / BroadcastBanner / BroadcastChatMessages.
const DISPLAY_PAGES = [
  { key: '*', label: 'Everywhere' },
  { key: 'home', label: 'Home' },
  { key: 'chat', label: 'Chat' },
  { key: 'bible', label: 'Bible Reader' },
  { key: 'reflection', label: 'Daily Reflection' },
  { key: 'community', label: 'Community' },
  { key: 'study_plans', label: 'Study Plans' },
  { key: 'settings', label: 'Settings' },
  { key: 'profile', label: 'Profile' },
  { key: 'recap', label: 'Weekly Recap' },
  { key: 'did_you_know', label: 'Did You Know' },
];

const CTA_OPTIONS = [
  { value: '', label: 'No Button' },
  // Core app pages
  { value: '/app', label: 'Home' },
  { value: '/chat', label: 'Open Chat' },
  { value: '/bible', label: 'Bible Reader' },
  { value: '/reflection', label: 'Daily Reflection' },
  { value: '/study-plans', label: 'Study Plans' },
  { value: '/recap', label: 'Weekly Recap' },
  { value: '/did-you-know', label: 'Did You Know' },
  { value: '/community', label: 'Community' },
  { value: '/faq', label: 'Help & FAQ' },
  // Conversion
  { value: '/plans', label: 'View Plans' },
  { value: '/signup', label: 'Sign Up' },
  { value: '/signup?trial=true', label: 'Sign Up (Free Trial)' },
  // Account
  { value: '/settings', label: 'Settings' },
  { value: '/analytics', label: 'My Analytics' },
  // Feedback & legal
  { value: '/reviews', label: 'Leave a Review' },
  { value: '/support', label: 'Help & Support' },
  { value: '/sources', label: 'Sources' },
  { value: '/community-guidelines', label: 'Community Guidelines' },
  { value: '/privacy', label: 'Privacy Policy' },
  { value: '/terms', label: 'Terms of Use' },
  // Custom
  { value: 'custom', label: 'Custom link (name your own)…' },
];

const TIMEZONES = [
  { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  { value: 'America/Anchorage', label: 'Alaska (AKST)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PST)' },
  { value: 'America/Denver', label: 'Mountain (MST)' },
  { value: 'America/Chicago', label: 'Central (CST)' },
  { value: 'America/New_York', label: 'Eastern (EST)' },
  { value: 'America/Sao_Paulo', label: 'Brazil (BRT)' },
  { value: 'Atlantic/Reykjavik', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Central EU (CET)' },
  { value: 'Europe/Helsinki', label: 'Eastern EU (EET)' },
  { value: 'Africa/Johannesburg', label: 'South Africa (SAST)' },
  { value: 'Africa/Lagos', label: 'West Africa (WAT)' },
  { value: 'Africa/Nairobi', label: 'East Africa (EAT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'Pacific/Auckland', label: 'New Zealand (NZST)' },
];

const TEMPLATES = [
  { title: 'Welcome to Eili!', message: 'We\'re glad you\'re here. Start exploring the Bible with your personal AI research companion.', style: 'info', cta_text: 'Get Started', cta_link: '/app' },
  { title: 'New Feature Available', message: 'We\'ve just launched a new feature! Check it out and let us know what you think.', style: 'feature', cta_text: 'Explore Now', cta_link: '/app' },
  { title: 'Special Offer', message: 'Upgrade to Premium and get unlimited access to all features. Limited time offer!', style: 'promo', cta_text: 'View Plans', cta_link: '/plans' },
  { title: 'Scheduled Maintenance', message: 'We\'ll be performing maintenance on [date]. The app may be briefly unavailable.', style: 'warning' },
  { title: 'Free Trial Available', message: 'Try all premium features free for 7 days. No credit card required.', style: 'promo', cta_text: 'Start Trial', cta_link: '/plans' },
];

function BroadcastPreview({ title, message, style, cta_text }) {
  const styleObj = STYLES.find(s => s.key === style) || STYLES[0];
  const Icon = styleObj.icon;
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: '#0f172a' }} data-testid="broadcast-preview">
      <div className="p-4 text-center text-[10px] text-slate-500 border-b border-slate-800">User's App View</div>
      <div className="p-6 flex items-center justify-center min-h-[200px]">
        <div
          className="w-full max-w-xs rounded-2xl p-5 relative"
          style={{
            background: 'rgba(15,23,42,0.85)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${styleObj.color}30`,
            boxShadow: `0 0 40px ${styleObj.color}10`,
          }}
        >
          <div className="flex items-start gap-3 mb-3">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0" style={{ background: styleObj.bg }}>
              <Icon className="h-4 w-4" style={{ color: styleObj.color }} />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-white leading-tight">{title || 'Title'}</h4>
            </div>
            <button className="shrink-0 p-0.5 rounded-full" style={{ color: 'rgba(255,255,255,0.3)' }}>
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed mb-4">{message || 'Your message here...'}</p>
          {cta_text && (
            <button
              className="w-full py-2 rounded-xl text-xs font-medium text-white"
              style={{ background: styleObj.color }}
            >
              {cta_text}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminBroadcastsPage() {
  const { isDark, colors } = useAdminTheme();
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: '', message: '', style: 'info', cta_action: '', cta_custom_link: '', cta_text: '', target_audience: 'all', display_locations: ['modal'], display_pages: ['*'], active: true, offer_tier: '', offer_duration_days: '', schedule_enabled: false, schedule_date: '', schedule_hour: '12', schedule_minute: '00', schedule_ampm: 'PM', schedule_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });

  const fetchBroadcasts = async () => {
    try {
      const res = await axios.get(`${API}/admin/broadcasts`, { withCredentials: true });
      setBroadcasts(res.data.broadcasts);
    } catch { toast.error('Failed to load broadcasts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBroadcasts(); }, []);

  const openEditor = (broadcast) => {
    if (broadcast) {
      setEditId(broadcast.broadcast_id);
      const ctaLink = broadcast.cta_link || '';
      const isCustom = ctaLink && !CTA_OPTIONS.find(o => o.value === ctaLink && o.value !== 'custom');

      // Parse scheduled_at back to form fields
      let schedEnabled = false, schedDate = '', schedHour = '12', schedMin = '00', schedAmpm = 'PM';
      let schedTz = broadcast.schedule_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (broadcast.scheduled_at) {
        schedEnabled = true;
        // Display in the saved timezone
        try {
          const dt = new Date(broadcast.scheduled_at);
          const parts = new Intl.DateTimeFormat('en-US', { timeZone: schedTz, year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true }).formatToParts(dt);
          const get = (type) => (parts.find(p => p.type === type) || {}).value || '';
          schedDate = `${get('year')}-${get('month')}-${get('day')}`;
          schedHour = String(parseInt(get('hour')) || 12);
          schedMin = get('minute');
          schedAmpm = get('dayPeriod')?.toUpperCase() || 'PM';
        } catch {
          const dt = new Date(broadcast.scheduled_at);
          schedDate = dt.toISOString().split('T')[0];
          let h = dt.getUTCHours();
          schedAmpm = h >= 12 ? 'PM' : 'AM';
          h = h % 12 || 12;
          schedHour = String(h);
          schedMin = String(dt.getUTCMinutes()).padStart(2, '0');
        }
      }

      setForm({
        title: broadcast.title, message: broadcast.message || '', style: broadcast.style,
        cta_action: isCustom ? 'custom' : ctaLink,
        cta_custom_link: isCustom ? ctaLink : '',
        cta_text: broadcast.cta_text || '',
        target_audience: broadcast.target_audience,
        display_locations: broadcast.display_locations || ['modal'],
        display_pages: broadcast.display_pages && broadcast.display_pages.length > 0 ? broadcast.display_pages : ['*'],
        active: broadcast.active, offer_tier: broadcast.offer_tier || '', offer_duration_days: broadcast.offer_duration_days || '',
        schedule_enabled: schedEnabled, schedule_date: schedDate, schedule_hour: schedHour, schedule_minute: schedMin, schedule_ampm: schedAmpm, schedule_timezone: schedTz,
      });
    } else {
      setEditId(null);
      setForm({ title: '', message: '', style: 'info', cta_action: '', cta_custom_link: '', cta_text: '', target_audience: 'all', display_locations: ['modal'], display_pages: ['*'], active: true, offer_tier: '', offer_duration_days: '', schedule_enabled: false, schedule_date: '', schedule_hour: '12', schedule_minute: '00', schedule_ampm: 'PM', schedule_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
    }
    setShowEditor(true);
  };

  const applyTemplate = (t) => setForm(prev => ({ ...prev, ...t, cta_text: t.cta_text || '', cta_link: t.cta_link || '' }));

  const handleSave = async () => {
    if (!form.title) { toast.error('Title is required'); return; }

    // Build scheduled_at ISO string from 12h clock fields in chosen timezone
    let scheduledAt = null;
    if (form.schedule_enabled && form.schedule_date) {
      let h = parseInt(form.schedule_hour) || 12;
      const m = parseInt(form.schedule_minute) || 0;
      if (form.schedule_ampm === 'AM' && h === 12) h = 0;
      if (form.schedule_ampm === 'PM' && h !== 12) h += 12;
      const localStr = `${form.schedule_date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`;
      try {
        const tempDate = new Date(localStr + 'Z');
        const inTz = new Date(new Intl.DateTimeFormat('en-CA', { timeZone: form.schedule_timezone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(tempDate).replace(', ', 'T') + 'Z');
        const offsetMs = tempDate - inTz;
        const dt = new Date(new Date(localStr + 'Z').getTime() + offsetMs);
        if (isNaN(dt.getTime())) { toast.error('Invalid schedule date/time'); return; }
        if (!editId && dt <= new Date()) { toast.error('Schedule time must be in the future'); return; }
        scheduledAt = dt.toISOString();
      } catch {
        const dt = new Date(`${form.schedule_date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00Z`);
        if (isNaN(dt.getTime())) { toast.error('Invalid schedule date/time'); return; }
        scheduledAt = dt.toISOString();
      }
    }

    const ctaLink = form.cta_action === 'custom' ? form.cta_custom_link : form.cta_action;
    const sendPush = form.display_locations.includes('push');
    const displayLocs = form.display_locations.filter(l => l !== 'push');
    // 🔒 Guard: at least one delivery target must be picked. Prior to
    // this, an admin who only toggled Popup Modal off (leaving nothing
    // else on) would save `display_locations: []` with `send_push: false`
    // and the broadcast would silently reach zero users. Now we block it.
    if (!sendPush && displayLocs.length === 0) {
      toast.error('Pick at least one delivery target (Popup, Chat Banner, System Message, or Push).');
      return;
    }

    // Build payload with only backend fields — no form-only fields
    const payload = {
      title: form.title,
      message: form.message || null,
      style: form.style,
      cta_text: form.cta_action ? (form.cta_text || null) : null,
      cta_link: form.cta_action ? (ctaLink || null) : null,
      target_audience: form.target_audience,
      display_locations: displayLocs.length ? displayLocs : [],
      // If admin unchecked every specific page we default back to
      // Everywhere (['*']) — matches the "always visible" contract
      // from before display_pages existed and avoids a footgun where
      // an unchecked list means "show nowhere".
      display_pages: form.display_pages && form.display_pages.length > 0 ? form.display_pages : ['*'],
      send_push: sendPush,
      active: form.schedule_enabled ? false : form.active,
      offer_tier: form.offer_tier || null,
      offer_duration_days: form.offer_duration_days ? parseInt(form.offer_duration_days) : null,
      scheduled_at: form.schedule_enabled ? (scheduledAt || null) : "",
      schedule_timezone: form.schedule_enabled ? form.schedule_timezone : null,
    };

    try {
      if (editId) {
        await axios.put(`${API}/admin/broadcasts/${editId}`, payload, { withCredentials: true });
        toast.success('Broadcast updated');
      } else {
        await axios.post(`${API}/admin/broadcasts`, payload, { withCredentials: true });
        toast.success(scheduledAt ? 'Broadcast scheduled' : 'Broadcast published');
      }
      setShowEditor(false);
      fetchBroadcasts();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this broadcast?')) return;
    try {
      await axios.delete(`${API}/admin/broadcasts/${id}`, { withCredentials: true });
      toast.success('Deleted');
      setBroadcasts(prev => prev.filter(b => b.broadcast_id !== id));
    } catch { toast.error('Failed to delete'); }
  };

  const toggleActive = async (b) => {
    try {
      await axios.put(`${API}/admin/broadcasts/${b.broadcast_id}`, { active: !b.active }, { withCredentials: true });
      setBroadcasts(prev => prev.map(x => x.broadcast_id === b.broadcast_id ? { ...x, active: !x.active } : x));
    } catch { toast.error('Failed to update'); }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="admin-broadcasts-page">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold" style={{ color: colors.text }}>Broadcasts</h1>
          <Button onClick={() => openEditor(null)} className="rounded-xl text-white gap-2 text-sm" style={{ background: colors.accent }} data-testid="create-broadcast-btn">
            <Plus className="h-4 w-4" /> New Broadcast
          </Button>
        </div>

        {/* Broadcast list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-5 w-5 animate-spin" style={{ color: colors.textMuted }} /></div>
          ) : broadcasts.length === 0 ? (
            <div className="text-center py-12">
              <Megaphone className="h-8 w-8 mx-auto mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>No broadcasts yet. Create one to reach your users.</p>
            </div>
          ) : (
            broadcasts.map(b => {
              const styleObj = STYLES.find(s => s.key === b.style) || STYLES[0];
              const Icon = styleObj.icon;
              const audience = AUDIENCES.find(a => a.key === b.target_audience)?.label || b.target_audience;
              const isScheduled = b.schedule_status === 'pending' && b.scheduled_at;
              const schedDate = isScheduled ? new Date(b.scheduled_at) : null;
              const schedTz = b.schedule_timezone || 'UTC';
              const formatSchedule = (dt) => {
                if (!dt) return '';
                try {
                  const formatted = dt.toLocaleString('en-US', { timeZone: schedTz, month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
                  const tzLabel = (TIMEZONES.find(t => t.value === schedTz) || {}).label || schedTz;
                  return `${formatted} (${tzLabel})`;
                } catch {
                  return dt.toISOString();
                }
              };
              return (
                <div key={b.broadcast_id} className="flex items-center gap-4 p-4 rounded-2xl" style={{ background: colors.card, border: `1px solid ${isScheduled ? colors.accent + '40' : colors.cardBorder}` }} data-testid={`broadcast-${b.broadcast_id}`}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: isScheduled ? colors.accent + '15' : styleObj.bg }}>
                    {isScheduled ? <CalendarClock className="h-4 w-4" style={{ color: colors.accent }} /> : <Icon className="h-4 w-4" style={{ color: styleObj.color }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: colors.text }}>{b.title}</p>
                    <p className="text-xs truncate" style={{ color: colors.textMuted }}>{b.message}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-[10px]" style={{ color: colors.textMuted }}><Users className="h-3 w-3 inline mr-1" />{audience}</span>
                      {(b.display_locations || ['modal']).map(loc => (
                        <span key={loc} className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: isDark ? 'rgba(51,65,85,0.5)' : 'rgba(241,245,249,1)', color: colors.textMuted }}>{loc}</span>
                      ))}
                      <span className="text-[10px]" style={{ color: colors.textMuted }}>{b.dismissals || 0} dismissed</span>
                      <span className="text-[10px] text-emerald-500 font-medium">{b.acceptances || 0} accepted</span>
                      {b.push_sent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-400 font-medium">push sent</span>}
                      {isScheduled && <span className="text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-1" style={{ background: colors.accent + '15', color: colors.accent }}><Clock className="h-2.5 w-2.5" />{formatSchedule(schedDate)}</span>}
                      {b.schedule_status === 'published' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-medium">auto-published</span>}
                      {b.offer_tier && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 font-medium">{b.offer_duration_days}d {b.offer_tier} trial</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => toggleActive(b)} className={`p-1.5 rounded-lg transition ${b.active ? 'text-green-400 bg-green-400/10' : 'bg-opacity-30'}`} style={!b.active ? { color: colors.textMuted, background: isDark ? 'rgba(51,65,85,0.3)' : 'rgba(241,245,249,1)' } : {}} title={b.active ? 'Active' : 'Inactive'} data-testid={`toggle-${b.broadcast_id}`}>
                      {b.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button onClick={() => openEditor(b)} className="p-1.5 rounded-lg transition" style={{ color: colors.textSecondary }} data-testid={`edit-${b.broadcast_id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(b.broadcast_id)} className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition" data-testid={`delete-${b.broadcast_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Editor Modal */}
      {showEditor && (
        <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={e => e.target === e.currentTarget && setShowEditor(false)} data-testid="broadcast-editor">
          <div className="w-full max-w-3xl my-8 rounded-2xl overflow-hidden" style={{ background: isDark ? '#1e293b' : '#ffffff', border: `1px solid ${colors.cardBorder}` }}>
            <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
              <h3 className="text-base font-semibold" style={{ color: colors.text }}>{editId ? 'Edit Broadcast' : 'New Broadcast'}</h3>
              <button onClick={() => setShowEditor(false)} className="p-1.5 rounded-full" style={{ color: colors.textSecondary }}><X className="h-4 w-4" /></button>
            </div>

            <div className="grid lg:grid-cols-2" style={{ borderColor: colors.cardBorder }}>
              {/* Form side */}
              <div className="p-6 space-y-4" style={{ borderRight: `1px solid ${colors.cardBorder}` }}>
                {/* Templates */}
                {!editId && (
                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: colors.textSecondary }}>Templates</label>
                    <div className="flex flex-wrap gap-1.5">
                      {TEMPLATES.map((t, i) => (
                        <button key={i} onClick={() => applyTemplate(t)} className="px-2.5 py-1 rounded-lg text-[11px] transition" style={{ background: isDark ? 'rgba(51,65,85,0.5)' : 'rgba(241,245,249,1)', color: colors.textSecondary }} data-testid={`template-${i}`}>
                          {t.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Style picker */}
                <div>
                  <label className="block text-xs font-medium mb-2" style={{ color: colors.textSecondary }}>Style</label>
                  <div className="flex gap-2" data-testid="style-picker">
                    {STYLES.map(s => (
                      <button key={s.key} onClick={() => setForm(f => ({ ...f, style: s.key }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition ${form.style === s.key ? 'ring-2' : 'opacity-60'}`}
                        style={{ background: s.bg, color: s.color, ringColor: s.color }}
                        data-testid={`style-${s.key}`}>
                        <s.icon className="h-3 w-3" /> {s.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Title</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="Broadcast title" data-testid="broadcast-title-input" />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Message <span className="opacity-50">(optional — push can be title-only)</span></label>
                  <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={3} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none resize-none" style={inputStyle} placeholder="Additional detail for your users..." data-testid="broadcast-message-input" />
                </div>

                {/* Button Action */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Button Action <span className="opacity-50">(optional)</span></label>
                  <select value={form.cta_action} onChange={e => {
                    const nextAction = e.target.value;
                    const opt = CTA_OPTIONS.find(o => o.value === nextAction);
                    // For a preset destination, seed the label with the preset's
                    // human name (e.g. "Open Chat"). For the "custom" option,
                    // clear the label so admins are prompted to type their own —
                    // otherwise the button shows the placeholder text.
                    setForm(f => ({
                      ...f,
                      cta_action: nextAction,
                      cta_text: !nextAction
                        ? ''
                        : nextAction === 'custom'
                          ? (f.cta_action === 'custom' ? f.cta_text : '')
                          : (f.cta_text && f.cta_action !== 'custom' ? f.cta_text : (opt?.label || '')),
                    }));
                  }} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={inputStyle} data-testid="broadcast-cta-action">
                    {CTA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>

                {form.cta_action && (
                  form.cta_action === 'custom' ? (
                    <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: colors.accent + '40', background: colors.accent + '08' }}>
                      <p className="text-[11px]" style={{ color: colors.textMuted }}>Custom link — set both the label users see on the button and the URL it opens.</p>
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Button Label</label>
                        <input value={form.cta_text} onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="e.g. Read the announcement" data-testid="broadcast-cta-text" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Destination URL</label>
                        <input value={form.cta_custom_link} onChange={e => setForm(f => ({ ...f, cta_custom_link: e.target.value }))} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="/bible or https://askeili.com/…" data-testid="broadcast-cta-custom" />
                        <p className="text-[10px] mt-1" style={{ color: colors.textMuted }}>In-app path (e.g. <code>/bible</code>) or a full <code>https://…</code> URL.</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Button Label</label>
                      <input value={form.cta_text} onChange={e => setForm(f => ({ ...f, cta_text: e.target.value }))} className="w-full rounded-xl px-4 py-2.5 text-sm placeholder-slate-500 focus:outline-none" style={inputStyle} placeholder="e.g. View Plans" data-testid="broadcast-cta-text" />
                    </div>
                  )
                )}

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Target Audience</label>
                  <select value={form.target_audience} onChange={e => setForm(f => ({ ...f, target_audience: e.target.value }))} className="w-full rounded-xl px-4 py-2.5 text-sm focus:outline-none" style={inputStyle} data-testid="broadcast-audience">
                    {AUDIENCES.map(a => <option key={a.key} value={a.key}>{a.label}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Where to Show</label>
                  <p className="text-[10px] mb-2" style={{ color: colors.textMuted }}>Pick every channel this broadcast should reach. Popup / Banner / System Message render inside the app; Push Notification sends to devices via OneSignal. You can combine any of them.</p>
                  <div className="flex flex-wrap gap-2" data-testid="broadcast-display-locations">
                    {DISPLAY_LOCATIONS.map(loc => {
                      const isSelected = form.display_locations.includes(loc.key);
                      return (
                        <button key={loc.key} onClick={() => setForm(f => {
                          const locs = f.display_locations.includes(loc.key)
                            ? f.display_locations.filter(l => l !== loc.key)
                            : [...f.display_locations, loc.key];
                          return { ...f, display_locations: locs.length ? locs : [loc.key] };
                        })}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${isSelected ? 'ring-2' : 'opacity-50'}`}
                          style={{ background: isSelected ? `${colors.accent}20` : colors.input, color: isSelected ? colors.accent : colors.textMuted, ringColor: colors.accent }}
                          data-testid={`loc-${loc.key}`}>
                          {loc.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Which pages the in-app broadcast is allowed on.
                    Push is unaffected (it goes to devices, not pages).
                    "Everywhere" is exclusive — picking it deselects
                    all specific pages, and picking any specific page
                    deselects "Everywhere". This mirrors the mental
                    model admins already use elsewhere in the app. */}
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Which Pages (in-app only)</label>
                  <p className="text-[10px] mb-2" style={{ color: colors.textMuted }}>Pick the surfaces where Popup / Banner / System Message can render. Push notifications ignore this list (they target devices, not pages). Pick "Everywhere" to show on every user-facing page.</p>
                  <div className="flex flex-wrap gap-2" data-testid="broadcast-display-pages">
                    {DISPLAY_PAGES.map(pg => {
                      const isSelected = (form.display_pages || []).includes(pg.key);
                      return (
                        <button
                          key={pg.key}
                          onClick={() => setForm(f => {
                            const current = f.display_pages || [];
                            let next;
                            if (pg.key === '*') {
                              // Selecting "Everywhere" clears the specific list.
                              next = ['*'];
                            } else if (current.includes(pg.key)) {
                              next = current.filter(p => p !== pg.key);
                            } else {
                              // Selecting a specific page implicitly turns off "Everywhere".
                              next = [...current.filter(p => p !== '*'), pg.key];
                            }
                            // Fall back to Everywhere if nothing is selected.
                            return { ...f, display_pages: next.length ? next : ['*'] };
                          })}
                          className={`px-3 py-1.5 rounded-xl text-xs font-medium transition ${isSelected ? 'ring-2' : 'opacity-50'}`}
                          style={{ background: isSelected ? `${colors.accent}20` : colors.input, color: isSelected ? colors.accent : colors.textMuted, ringColor: colors.accent }}
                          data-testid={`page-${pg.key}`}
                        >
                          {pg.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Schedule */}
                <div className="rounded-xl border p-3 space-y-3" style={{ borderColor: form.schedule_enabled ? colors.accent + '40' : colors.cardBorder, background: form.schedule_enabled ? colors.accent + '08' : 'transparent' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-3.5 w-3.5" style={{ color: form.schedule_enabled ? colors.accent : colors.textMuted }} />
                      <span className="text-xs font-medium" style={{ color: form.schedule_enabled ? colors.accent : colors.textMuted }}>Schedule for Later</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, schedule_enabled: !f.schedule_enabled }))}
                      className={`w-9 h-5 rounded-full transition-colors relative ${form.schedule_enabled ? '' : 'bg-slate-600'}`}
                      style={form.schedule_enabled ? { background: colors.accent } : {}}
                      data-testid="schedule-toggle"
                    >
                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.schedule_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>

                  {form.schedule_enabled && (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Date</label>
                        <input
                          type="date"
                          value={form.schedule_date}
                          onChange={e => setForm(f => ({ ...f, schedule_date: e.target.value }))}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                          style={inputStyle}
                          data-testid="schedule-date"
                        />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Hour</label>
                          <select
                            value={form.schedule_hour}
                            onChange={e => setForm(f => ({ ...f, schedule_hour: e.target.value }))}
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={inputStyle}
                            data-testid="schedule-hour"
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(h => (
                              <option key={h} value={String(h)}>{h}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Minute</label>
                          <select
                            value={form.schedule_minute}
                            onChange={e => setForm(f => ({ ...f, schedule_minute: e.target.value }))}
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={inputStyle}
                            data-testid="schedule-minute"
                          >
                            {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                              <option key={m} value={m}>{m}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>AM/PM</label>
                          <select
                            value={form.schedule_ampm}
                            onChange={e => setForm(f => ({ ...f, schedule_ampm: e.target.value }))}
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={inputStyle}
                            data-testid="schedule-ampm"
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Timezone</label>
                          <select
                            value={form.schedule_timezone}
                            onChange={e => setForm(f => ({ ...f, schedule_timezone: e.target.value }))}
                            className="w-full rounded-lg px-3 py-2 text-xs focus:outline-none"
                            style={inputStyle}
                            data-testid="schedule-timezone"
                          >
                            {TIMEZONES.map(tz => (
                              <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <p className="text-[10px] flex items-center gap-1" style={{ color: colors.textMuted }}>
                        <Clock className="h-3 w-3" /> The broadcast will auto-publish at the scheduled time in your chosen timezone.
                      </p>
                    </div>
                  )}
                </div>

                {/* Trial Offer */}
                <div className="rounded-xl bg-amber-500/5 border border-amber-500/20 p-3 space-y-3">
                  <p className="text-xs font-medium text-amber-400">Attach Trial Offer (optional)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Plan</label>
                      <select value={form.offer_tier} onChange={e => setForm(f => ({ ...f, offer_tier: e.target.value }))} className="w-full rounded-lg px-3 py-2 text-xs" style={inputStyle} data-testid="broadcast-offer-tier">
                        <option value="">No offer</option>
                        <option value="premium">Premium ($9.99)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[11px] mb-1" style={{ color: colors.textMuted }}>Duration (days)</label>
                      <input type="number" value={form.offer_duration_days} onChange={e => setForm(f => ({ ...f, offer_duration_days: e.target.value }))} min={1} max={90} className="w-full rounded-lg px-3 py-2 text-xs placeholder-slate-500" style={inputStyle} placeholder="e.g. 7" disabled={!form.offer_tier} data-testid="broadcast-offer-days" />
                    </div>
                  </div>
                  {form.offer_tier && <p className="text-[10px]" style={{ color: colors.textMuted }}>Users who click the CTA will instantly receive a {form.offer_duration_days || '?'}-day {form.offer_tier} trial.</p>}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={() => setShowEditor(false)} variant="ghost" className="flex-1 rounded-xl" style={{ color: colors.textSecondary }}>Cancel</Button>
                  <Button onClick={handleSave} className="flex-1 rounded-xl text-white gap-2" style={{ background: colors.accent }} data-testid="broadcast-save-btn">
                    {form.schedule_enabled ? <CalendarClock className="h-4 w-4" /> : <Megaphone className="h-4 w-4" />}
                    {editId ? 'Update' : (form.schedule_enabled ? 'Schedule' : 'Publish')}
                  </Button>
                </div>
              </div>

              {/* Preview side */}
              <div className="p-6">
                <label className="block text-xs font-medium mb-3" style={{ color: colors.textSecondary }}>Live Preview</label>
                <BroadcastPreview {...form} />
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
